// Dashboard/Panel del Cliente
// RecomiendaT - Sistema de recomendaciones

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import databaseService from '../utils/database';
import './Dashboard.css';
import EventsSection from './EventsSection';
import EmojiPicker from 'emoji-picker-react';
import { 
  FaUser, 
  FaSignOutAlt, 
  FaRegNewspaper, 
  FaRegCalendarAlt, 
  FaIdBadge,
  FaShoppingCart,
  FaSearch,
  FaThumbsUp,
  FaComment,
  FaShare,
  FaReply
} from 'react-icons/fa';

export default function Dashboard() {
  const { user, logout, updateProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const getInitialTab = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('tab') || 'mis-recomendaciones';
    } catch (_) {
      return 'mis-recomendaciones';
    }
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [loading, setLoading] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedRecommendation, setSelectedRecommendation] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [likesLoading, setLikesLoading] = useState({});
  const [likesData, setLikesData] = useState({});
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  
  // Estados para recomendación rápida
  const [quickRecommendation, setQuickRecommendation] = useState({
    titulo: '',
    descripcion: '',
    categoria: '',
    enlace_url: ''
  });
  const [quickLoading, setQuickLoading] = useState(false);
  
  // Emoji pickers independientes
  const [showQuickEmojiPicker, setShowQuickEmojiPicker] = useState(false);
  const [commentEmoji, setCommentEmoji] = useState({ visible: false, top: 0, left: 0, recId: null });
  
  // Estados para comentarios inline
  const [inlineComments, setInlineComments] = useState({});
  const [newInlineComment, setNewInlineComment] = useState({});
  const [showInlineComments, setShowInlineComments] = useState({});
  
  // Estados para likes de comentarios
  const [commentLikes, setCommentLikes] = useState({});
  const [commentLikesData, setCommentLikesData] = useState({});
  const [commentLikesLoading, setCommentLikesLoading] = useState({});
  
  // Estados para respuestas de comentarios
  const [commentReplies, setCommentReplies] = useState({});
  const [newReply, setNewReply] = useState({});
  const [showReplies, setShowReplies] = useState({});
  const [repliesLoading, setRepliesLoading] = useState({});
  const [commentSending, setCommentSending] = useState({});
  const [replySending, setReplySending] = useState({});
  
  // Estados para compartir
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareRecommendation, setShareRecommendation] = useState(null);
  const [shareUrl, setShareUrl] = useState('');
  const [profileTab, setProfileTab] = useState('perfil'); // 'perfil' | 'config'
  const [cartItems, setCartItems] = useState([]);
  const cartCount = cartItems.reduce((sum, it) => sum + (it.quantity || 1), 0);
  const [profileForm, setProfileForm] = useState({ nombre: user?.nombre || '', email: user?.email || '', telefono: user?.telefono || '' });
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  useEffect(() => {
    // Sincroniza el formulario cuando cambie el usuario
    setProfileForm({ nombre: user?.nombre || '', email: user?.email || '', telefono: user?.telefono || '' });
    setProfileDirty(false);
  }, [user]);

  // Emoji Picker unificado
  const emojiPickerProps = {
    width: 300,
    height: 350,
    searchDisabled: false,
    skinTonesDisabled: false
  };
  const renderEmojiPicker = (onEmojiClick) => (
    <EmojiPicker onEmojiClick={onEmojiClick} {...emojiPickerProps} />
  );

  // Placeholder con escritura simulada para el título
  const defaultTitlePlaceholder = '¿Qué quieres recomendar?';
  const typingExamples = useMemo(() => [
    'Ej: Restaurante “La Terraza del Centro” 🍝',
    'Ej: Coworking "CIMA Businnes Center" “Espacio 24/7” 💼',
    'Ej: Estudio “Creativa” Diseño gráfico 🎨'
  ], []);
  const [titlePlaceholder, setTitlePlaceholder] = useState(defaultTitlePlaceholder);
  const [titleFocused, setTitleFocused] = useState(false);
  const [exampleIndex, setExampleIndex] = useState(0);
  useEffect(() => {
    if (titleFocused || quickRecommendation.titulo.trim() !== '') {
      setTitlePlaceholder(defaultTitlePlaceholder);
      return; // Detener loop mientras el usuario interactúa
    }
    let typingIntervalId;
    let mainTimeoutId;

    const blinkTwice = (afterBlink) => {
      let count = 0;
      const doBlink = () => {
        setTitlePlaceholder('');
        setTimeout(() => {
          setTitlePlaceholder(defaultTitlePlaceholder);
          count += 1;
          if (count < 2) {
            setTimeout(doBlink, 180);
          } else if (typeof afterBlink === 'function') {
            afterBlink();
          }
        }, 180);
      };
      doBlink();
    };

    const typeExample = (text, onDone) => {
      let i = 0;
      setTitlePlaceholder('');
      typingIntervalId = setInterval(() => {
        i += 1;
        setTitlePlaceholder(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(typingIntervalId);
          mainTimeoutId = setTimeout(() => {
            setTitlePlaceholder(defaultTitlePlaceholder);
            blinkTwice(onDone);
          }, 1200);
        }
      }, 55);
    };

    const startLoop = () => {
      const example = typingExamples[exampleIndex % typingExamples.length];
      typeExample(example, () => {
        setExampleIndex((prev) => (prev + 1) % typingExamples.length);
      });
    };

    // Pequeña espera inicial para no ser invasivo
    mainTimeoutId = setTimeout(startLoop, 1500);

    return () => {
      clearInterval(typingIntervalId);
      clearTimeout(mainTimeoutId);
    };
  }, [titleFocused, quickRecommendation.titulo, exampleIndex, typingExamples]);

  const handleLogout = async () => {
    setLoading(true);
    await logout();
    setLoading(false);
  };

  const handleLogoClick = () => {
    navigate('/');
  };

  // Cargar likes (definido antes para evitar no-use-before-define)
  const loadLikes = useCallback(async (recommendationId) => {
    try {
      const response = await databaseService.getLikes(recommendationId);
      if (response.success) {
        setLikesData(prev => ({ ...prev, [recommendationId]: response.likes }));
      }
    } catch (error) { /* noop */ }
  }, []);

  const loadRecommendations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await databaseService.getRecommendations();
      if (response.success) {
        setRecommendations(response.recommendations);
        
        // Cargar likes para todas las recomendaciones que tengan likes (en paralelo)
        const recommendationsWithLikes = response.recommendations.filter(rec => rec.likes_count > 0);
        const likesPromises = recommendationsWithLikes.map(rec => loadLikes(rec.id));
        await Promise.all(likesPromises);
      }
    } catch (error) { /* noop */ } finally {
      setLoading(false);
    }
  }, [loadLikes]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await databaseService.getCategories();
      if (response.success) {
        setCategories(response.categories);
      }
    } catch (error) { /* noop */ }
  }, []);

  // Cargar recomendaciones y categorías
  useEffect(() => {
    loadRecommendations();
    loadCategories();
    
    // Cargar likes desde localStorage
    const savedLikes = localStorage.getItem('commentLikes');
    const savedLikesData = localStorage.getItem('commentLikesData');
    
    if (savedLikes) {
      try {
        setCommentLikes(JSON.parse(savedLikes));
      } catch (error) { /* noop */ }
    }
    
    if (savedLikesData) {
      try {
        setCommentLikesData(JSON.parse(savedLikesData));
      } catch (error) { /* noop */ }
    }
  }, [loadRecommendations, loadCategories]);

  // Sincronizar pestaña con la URL (?tab=...) solo cuando la URL cambia externamente
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl && tabFromUrl !== activeTab) {
      setActiveTab(tabFromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const lastSearchRef = useRef('');
  useEffect(() => {
    // Si el carrito está vacío y la URL apunta a carrito, redirigir a eventos
    if (activeTab === 'carrito' && cartItems.length === 0) {
      setActiveTab('eventos');
      return;
    }
    
    // Solo actualizar la URL si no viene de un cambio de URL externo
    const params = new URLSearchParams(window.location.search);
    const currentTabInUrl = params.get('tab');
    
    if (currentTabInUrl !== activeTab) {
      params.set('tab', activeTab);
      const newSearch = params.toString();
      if (lastSearchRef.current !== newSearch) {
        lastSearchRef.current = newSearch;
        navigate({ search: newSearch }, { replace: true });
      }
    }
  }, [activeTab, cartItems.length, navigate]);

  // Cerrar emoji pickers al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsidePicker = event.target.closest('.emoji-picker-container');
      const clickedEmojiButton = event.target.closest('.emoji-button');
      if (!clickedInsidePicker && !clickedEmojiButton) {
        setShowQuickEmojiPicker(false);
        setCommentEmoji(prev => ({ ...prev, visible: false }));
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // (La verificación de usuario se mueve al final, antes del return principal, para no romper las reglas de Hooks)

  // Filtrar recomendaciones
  const filteredRecommendations = recommendations.filter(rec => {
    const matchesSearch = rec.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rec.descripcion.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || rec.categoria === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Filtrar recomendaciones favoritas (con like)
  // Eliminado favoritos

  // Manejar like
  const handleLike = async (recommendationId) => {
    try {
      setLikesLoading(prev => ({ ...prev, [recommendationId]: true }));
      const response = await databaseService.toggleLike(recommendationId);
      
      if (response.success) {
        // Actualizar el conteo de likes en la recomendación
        setRecommendations(prev => prev.map(rec => 
          rec.id === recommendationId 
            ? { ...rec, likes_count: response.likes_count, user_liked: response.action === 'added' }
            : rec
        ));
        
        // Recargar los likes para actualizar la lista de nombres
        await loadLikes(recommendationId);
      }
    } catch (error) { /* noop */ } finally {
      setLikesLoading(prev => ({ ...prev, [recommendationId]: false }));
    }
  };

  // Cargar comentarios
  const loadComments = async (recommendationId) => {
    try {
      setCommentsLoading(true);
      const response = await databaseService.getComments(recommendationId);
      if (response.success) {
        setComments(response.comments);
      }
    } catch (error) { /* noop */ } finally {
      setCommentsLoading(false);
    }
  };

  // Crear comentario
  const handleCreateComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() || !selectedRecommendation) return;

    try {
      setCommentLoading(true);
      const response = await databaseService.createComment(selectedRecommendation.id, {
        comentario: newComment.trim()
      });
      
      if (response.success) {
        setNewComment('');
        await loadComments(selectedRecommendation.id);
        
        // Actualizar el conteo de comentarios en la recomendación
        setRecommendations(prev => prev.map(rec => 
          rec.id === selectedRecommendation.id 
            ? { ...rec, comments_count: (rec.comments_count || 0) + 1 }
            : rec
        ));
      }
    } catch (error) { /* noop */ } finally {
      setCommentLoading(false);
    }
  };

  // (openCommentsModal ya no se usa; se mantiene inline comments)

  // (loadLikes definido arriba)

  // Abrir modal de likes
  const openLikesModal = async (recommendation) => {
    setSelectedRecommendation(recommendation);
    await loadLikes(recommendation.id);
    setShowLikesModal(true);
  };

  // Función para normalizar URLs de forma inteligente
  const normalizeUrl = (url) => {
    if (!url || !url.trim()) return '';
    
    let normalizedUrl = url.trim();
    
    // Si ya tiene protocolo, devolver tal como está
    if (normalizedUrl.match(/^https?:\/\//i)) {
      return normalizedUrl;
    }
    
    // Si empieza con www., agregar https://
    if (normalizedUrl.match(/^www\./i)) {
      return 'https://' + normalizedUrl;
    }
    
    // Si no tiene protocolo ni www, agregar https://
    if (!normalizedUrl.match(/^https?:\/\//i)) {
      return 'https://' + normalizedUrl;
    }
    
    return normalizedUrl;
  };

  // Funciones para emoji picker
  const handleEmojiClick = (emojiData) => {
    const emoji = emojiData.emoji;
    setQuickRecommendation(prev => ({
      ...prev,
      descripcion: prev.descripcion + emoji
    }));
  };

  const toggleEmojiPicker = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setShowQuickEmojiPicker(prev => !prev);
  };

  // Funciones para comentarios inline
  const loadInlineComments = async (recommendationId) => {
    try {
      const response = await databaseService.getComments(recommendationId);
      if (response.success) {
        const mainComments = response.comments;
        
        setInlineComments(prev => ({
          ...prev,
          [recommendationId]: mainComments
        }));
        
        // Cargar respuestas para cada comentario principal
        const repliesPromises = mainComments.map(async (comment) => {
          try {
            const repliesResponse = await databaseService.getCommentReplies(comment.id);
            if (repliesResponse.success) {
              return { commentId: comment.id, replies: repliesResponse.replies };
            }
            return { commentId: comment.id, replies: [] };
          } catch (error) {
            return { commentId: comment.id, replies: [] };
          }
        });
        
        const repliesResults = await Promise.all(repliesPromises);
        
        // Organizar respuestas por comentario padre
        const repliesByParent = {};
        repliesResults.forEach(({ commentId, replies }) => {
          repliesByParent[commentId] = replies;
        });
        
        setCommentReplies(prev => ({
          ...prev,
          [recommendationId]: repliesByParent
        }));
        
        // Mostrar automáticamente las respuestas que tienen contenido
        const newShowReplies = {};
        Object.keys(repliesByParent).forEach(commentId => {
          if (repliesByParent[commentId].length > 0) {
            newShowReplies[commentId] = true;
          }
        });
        
        if (Object.keys(newShowReplies).length > 0) {
          setShowReplies(prev => ({
            ...prev,
            ...newShowReplies
          }));
        }
        
        // Cargar likes para cada comentario principal (temporalmente deshabilitado por CORS)
        // const commentsWithLikes = await Promise.all(
        //   mainComments.map(async (comment) => {
        //     try {
        //       const likesResponse = await databaseService.getCommentLikes(comment.id);
        //       if (likesResponse.success) {
        //         setCommentLikes(prev => ({
        //           ...prev,
        //           [comment.id]: likesResponse.likes.length
        //         }));
        //         setCommentLikesData(prev => ({
        //           ...prev,
        //           [comment.id]: likesResponse.likes
        //         }));
        //       }
        //       return comment;
        //     } catch (error) {
        //       /* noop */
        //       return comment;
        //     }
        //   })
        // );
      }
    } catch (error) { /* noop */ }
  };

  const toggleInlineComments = async (recommendationId) => {
    const isShowing = showInlineComments[recommendationId];
    setShowInlineComments(prev => ({
      ...prev,
      [recommendationId]: !isShowing
    }));
    
    if (!isShowing) {
      await loadInlineComments(recommendationId);
    }
  };

  const handleInlineCommentChange = (recommendationId, value) => {
    setNewInlineComment(prev => ({
      ...prev,
      [recommendationId]: value
    }));
  };

  // Función para hacer scroll automático al final de los comentarios
  const scrollToBottom = (recommendationId) => {
    setTimeout(() => {
      const commentsContainer = document.querySelector(`[data-recommendation-id="${recommendationId}"] .comments-list`);
      if (commentsContainer) {
        commentsContainer.scrollTop = commentsContainer.scrollHeight;
      }
    }, 100);
  };

  const scrollToNewReply = (recommendationId, commentId) => {
    setTimeout(() => {
      const replyElement = document.querySelector(`[data-recommendation-id="${recommendationId}"] [data-comment-id="${commentId}"] .reply-item:last-child`);
      if (replyElement) {
        replyElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }, 100);
  };

  const handleInlineCommentSubmit = async (recommendationId) => {
    const commentText = newInlineComment[recommendationId];
    if (!commentText || !commentText.trim() || !user) return;

    // Crear comentario optimista
    const optimisticComment = {
      id: `temp_${Date.now()}`, // ID temporal
      comentario: commentText,
      autor: {
        nombre: user.nombre || 'Usuario',
        id: user.id || 'temp_id'
      },
      fecha_creacion: new Date().toISOString(),
      comentario_padre_id: null
    };

    // Agregar comentario optimista inmediatamente
    setInlineComments(prev => {
      const newState = {
        ...prev,
        [recommendationId]: [...(prev[recommendationId] || []), optimisticComment]
      };
      return newState;
    });

    // Limpiar el campo de comentario inmediatamente
    setNewInlineComment(prev => ({
      ...prev,
      [recommendationId]: ''
    }));

    // Hacer scroll automático al final
    scrollToBottom(recommendationId);

    // Indicar que se está enviando
    setCommentSending(prev => ({
      ...prev,
      [recommendationId]: true
    }));

    try {
      const response = await databaseService.createComment(recommendationId, {
        comentario: commentText
      });
      
      if (response.success) {
        // Reemplazar comentario optimista con el real (usar el optimista ya que el backend solo devuelve ID)
        setInlineComments(prev => ({
          ...prev,
          [recommendationId]: prev[recommendationId].map(comment => 
            comment.id === optimisticComment.id ? {
              ...optimisticComment,
              id: response.comment_id // Solo actualizar el ID real
            } : comment
          )
        }));

        // Actualizar el contador de comentarios en la recomendación
        setRecommendations(prev => prev.map(rec => 
          rec.id === recommendationId 
            ? { ...rec, comments_count: (rec.comments_count || 0) + 1 }
            : rec
        ));
      } else {
        // Si falla, remover comentario optimista
        setInlineComments(prev => ({
          ...prev,
          [recommendationId]: prev[recommendationId].filter(comment => comment.id !== optimisticComment.id)
        }));
        // Restaurar texto en el campo
        setNewInlineComment(prev => ({
          ...prev,
          [recommendationId]: commentText
        }));
        alert('Error al publicar el comentario. Inténtalo de nuevo.');
      }
    } catch (error) { /* noop */
      // Si falla, remover comentario optimista
      setInlineComments(prev => ({
        ...prev,
        [recommendationId]: prev[recommendationId].filter(comment => comment.id !== optimisticComment.id)
      }));
      // Restaurar texto en el campo
      setNewInlineComment(prev => ({
        ...prev,
        [recommendationId]: commentText
      }));
      alert('Error al publicar el comentario. Inténtalo de nuevo.');
    } finally {
      // Finalizar estado de envío
      setCommentSending(prev => ({
        ...prev,
        [recommendationId]: false
      }));
    }
  };

  // Funciones para likes de comentarios (versión temporal sin backend)
  const handleCommentLike = async (commentId) => {
    try {
      setCommentLikesLoading(prev => ({ ...prev, [commentId]: true }));
      
      // Versión temporal: solo actualizar el estado local
      const currentLikes = commentLikes[commentId] || 0;
      const isLiked = commentLikesData[commentId]?.some(like => like.email === user?.email) || false;
      
      let newLikes, newLikesData;
      
      if (isLiked) {
        // Quitar like
        newLikes = Math.max(0, currentLikes - 1);
        newLikesData = (commentLikesData[commentId] || []).filter(like => like.email !== user?.email);
      } else {
        // Agregar like
        newLikes = currentLikes + 1;
        newLikesData = [
          ...(commentLikesData[commentId] || []),
          {
            nombre: user?.nombre || 'Usuario',
            email: user?.email || '',
            fecha_creacion: new Date().toISOString()
          }
        ];
      }
      
      // Actualizar estado
      setCommentLikes(prev => ({
        ...prev,
        [commentId]: newLikes
      }));
      
      setCommentLikesData(prev => ({
        ...prev,
        [commentId]: newLikesData
      }));
      
      // Guardar en localStorage para persistencia
      const likesToSave = {
        ...commentLikes,
        [commentId]: newLikes
      };
      const likesDataToSave = {
        ...commentLikesData,
        [commentId]: newLikesData
      };
      
      localStorage.setItem('commentLikes', JSON.stringify(likesToSave));
      localStorage.setItem('commentLikesData', JSON.stringify(likesDataToSave));
      
      
      
    } catch (error) { /* noop */ } finally {
      setCommentLikesLoading(prev => ({ ...prev, [commentId]: false }));
    }
  };

  // Funciones para respuestas de comentarios
  const loadCommentReplies = async (commentId) => {
    try {
      setRepliesLoading(prev => ({ ...prev, [commentId]: true }));
      const response = await databaseService.getCommentReplies(commentId);
      if (response.success) {
        setCommentReplies(prev => ({
          ...prev,
          [commentId]: response.replies
        }));
      }
    } catch (error) { /* noop */ } finally {
      setRepliesLoading(prev => ({ ...prev, [commentId]: false }));
    }
  };

  const toggleReplies = async (commentId) => {
    const isShowing = showReplies[commentId];
    setShowReplies(prev => ({
      ...prev,
      [commentId]: !isShowing
    }));
    
    if (!isShowing) {
      await loadCommentReplies(commentId);
    }
  };

  const handleReplyChange = (commentId, value) => {
    setNewReply(prev => ({
      ...prev,
      [commentId]: value
    }));
  };

  const handleReplySubmit = async (commentId) => {
    const replyText = newReply[commentId]?.trim();
    if (!replyText || !user) return;

    // Buscar el recommendationId en las recomendaciones
    const recommendationId = Object.keys(inlineComments).find(key => 
      inlineComments[key].some(comment => comment.id === commentId)
    );

    if (!recommendationId) {
      return;
    }

    // Crear respuesta optimista
    const optimisticReply = {
      id: `temp_reply_${Date.now()}`, // ID temporal
      comentario: replyText,
      autor: {
        nombre: user.nombre || 'Usuario',
        id: user.id || 'temp_id'
      },
      fecha_creacion: new Date().toISOString(),
      comentario_padre_id: commentId
    };

    // Agregar respuesta optimista inmediatamente
    setCommentReplies(prev => ({
      ...prev,
      [recommendationId]: {
        ...prev[recommendationId],
        [commentId]: [...(prev[recommendationId]?.[commentId] || []), optimisticReply]
      }
    }));

    // Limpiar el campo de respuesta inmediatamente
    setNewReply(prev => ({
      ...prev,
      [commentId]: ''
    }));

    // Hacer scroll automático a la nueva respuesta
    scrollToNewReply(recommendationId, commentId);

    // Indicar que se está enviando
    setReplySending(prev => ({
      ...prev,
      [commentId]: true
    }));

    try {
      const response = await databaseService.createComment(recommendationId, {
        comentario: replyText,
        comentario_padre_id: commentId
      });
      
      if (response.success) {
        // Reemplazar respuesta optimista con la real (usar el optimista ya que el backend solo devuelve ID)
        setCommentReplies(prev => ({
          ...prev,
          [recommendationId]: {
            ...prev[recommendationId],
            [commentId]: prev[recommendationId][commentId].map(reply => 
              reply.id === optimisticReply.id ? {
                ...optimisticReply,
                id: response.comment_id // Solo actualizar el ID real
              } : reply
            )
          }
        }));

        // Actualizar el contador de comentarios en la recomendación (las respuestas también cuentan como comentarios)
        setRecommendations(prev => prev.map(rec => 
          rec.id === recommendationId 
            ? { ...rec, comments_count: (rec.comments_count || 0) + 1 }
            : rec
        ));
      } else {
        // Si falla, remover respuesta optimista
        setCommentReplies(prev => ({
          ...prev,
          [recommendationId]: {
            ...prev[recommendationId],
            [commentId]: prev[recommendationId][commentId].filter(reply => reply.id !== optimisticReply.id)
          }
        }));
        // Restaurar texto en el campo
        setNewReply(prev => ({
          ...prev,
          [commentId]: replyText
        }));
        alert('Error al publicar la respuesta. Inténtalo de nuevo.');
      }
    } catch (error) { /* noop */
      // Si falla, remover respuesta optimista
      setCommentReplies(prev => ({
        ...prev,
        [recommendationId]: {
          ...prev[recommendationId],
          [commentId]: prev[recommendationId][commentId].filter(reply => reply.id !== optimisticReply.id)
        }
      }));
      // Restaurar texto en el campo
      setNewReply(prev => ({
        ...prev,
        [commentId]: replyText
      }));
      alert('Error al publicar la respuesta. Inténtalo de nuevo.');
    } finally {
      // Finalizar estado de envío
      setReplySending(prev => ({
        ...prev,
        [commentId]: false
      }));
    }
  };

  // Funciones para compartir
  const openShareModal = (recommendation) => {
    setShareRecommendation(recommendation);
    // Generar URL única para compartir
    const baseUrl = window.location.origin;
    const shareUrl = `${baseUrl}/share/${recommendation.id}`;
    setShareUrl(shareUrl);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareRecommendation(null);
    setShareUrl('');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      // Aquí podrías agregar una notificación de éxito
    } catch (error) { /* noop */ }
  };

  const shareToSocial = (platform) => {
    const text = `¡Mira esta recomendación en RecomiendaT! "${shareRecommendation.titulo}"`;
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(shareUrl);
    
    let socialUrl = '';
    
    switch (platform) {
      case 'facebook':
        // Usar la nueva API de Facebook que funciona mejor
        socialUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`;
        break;
      case 'twitter':
        socialUrl = `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
        break;
      case 'whatsapp':
        socialUrl = `https://wa.me/?text=${encodedText}%20${encodedUrl}`;
        break;
      case 'telegram':
        socialUrl = `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`;
        break;
      case 'linkedin':
        socialUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      default:
        return;
    }
    
    // Para Facebook, usar una ventana más grande
    if (platform === 'facebook') {
      window.open(socialUrl, '_blank', 'width=700,height=500,scrollbars=yes,resizable=yes');
    } else {
      window.open(socialUrl, '_blank', 'width=600,height=400');
    }
  };

  // Manejar recomendación rápida
  const handleQuickRecommendation = async (e) => {
    e.preventDefault();
    if (!quickRecommendation.titulo.trim() || !quickRecommendation.descripcion.trim() || !quickRecommendation.categoria) {
      return;
    }

    setQuickLoading(true);
    try {
      // Normalizar la URL antes de enviar
      const recommendationData = {
        ...quickRecommendation,
        enlace_url: normalizeUrl(quickRecommendation.enlace_url)
      };

      const response = await databaseService.createRecommendation(recommendationData);
      if (response.success) {
        // Limpiar formulario
        setQuickRecommendation({
          titulo: '',
          descripcion: '',
          categoria: '',
          enlace_url: ''
        });
        // Recargar recomendaciones
        await loadRecommendations();
      }
    } catch (error) { /* noop */ } finally {
      setQuickLoading(false);
    }
  };

  // Función para generar texto atractivo del enlace
  const getLinkDisplayText = (url) => {
    if (!url) return '';
    
    const normalizedUrl = normalizeUrl(url);
    const domain = new URL(normalizedUrl).hostname.toLowerCase();
    
    // Detectar plataformas populares
    if (domain.includes('facebook.com') || domain.includes('fb.com')) {
      return '📘 Ver en Facebook';
    } else if (domain.includes('instagram.com')) {
      return '📷 Ver en Instagram';
    } else if (domain.includes('youtube.com') || domain.includes('youtu.be')) {
      return '🎥 Ver en YouTube';
    } else if (domain.includes('twitter.com') || domain.includes('x.com')) {
      return '🐦 Ver en Twitter';
    } else if (domain.includes('tiktok.com')) {
      return '🎵 Ver en TikTok';
    } else if (domain.includes('linkedin.com')) {
      return '💼 Ver en LinkedIn';
    } else if (domain.includes('whatsapp.com')) {
      return '💬 Ver en WhatsApp';
    } else if (domain.includes('google.com') || domain.includes('maps.google.com')) {
      return '🗺️ Ver en Google Maps';
    } else if (domain.includes('tripadvisor.com')) {
      return '⭐ Ver en TripAdvisor';
    } else if (domain.includes('zomato.com') || domain.includes('ubereats.com') || domain.includes('rappi.com')) {
      return '🍽️ Ver en la App de Comida';
    } else if (domain.includes('booking.com') || domain.includes('airbnb.com') || domain.includes('hotels.com')) {
      return '🏨 Ver en la App de Viajes';
    } else if (domain.includes('amazon.com') || domain.includes('mercadolibre.com') || domain.includes('ebay.com')) {
      return '🛒 Ver en la Tienda';
    } else {
      // Para sitios web genéricos, mostrar el dominio
      const cleanDomain = domain.replace('www.', '');
      return `🌐 Ver en ${cleanDomain}`;
    }
  };

  // Función para formatear tiempo de publicación (como Facebook)
  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInSeconds = Math.floor((now - postDate) / 1000);
    
    if (diffInSeconds < 60) {
      return 'hace un momento';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `hace ${minutes} minuto${minutes > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `hace ${hours} hora${hours > 1 ? 's' : ''}`;
    } else if (diffInSeconds < 2592000) {
      const days = Math.floor(diffInSeconds / 86400);
      return `hace ${days} día${days > 1 ? 's' : ''}`;
    } else {
      return postDate.toLocaleDateString('es-ES', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
  };

  // Formatear nombres de likes como Facebook
  const formatLikesText = (likes, currentUser) => {
    if (!likes || likes.length === 0) return '';
    
    const currentUserLike = likes.find(like => like.email === currentUser?.email);
    const otherLikes = likes.filter(like => like.email !== currentUser?.email);
    
    let text = '';
    
    if (currentUserLike) {
      text = 'Tú';
      if (otherLikes.length > 0) {
        text += `, ${otherLikes[0].nombre}`;
        if (otherLikes.length > 1) {
          text += ` y ${otherLikes.length - 1} persona${otherLikes.length > 2 ? 's' : ''} más`;
        }
      }
    } else {
      if (likes.length === 1) {
        text = likes[0].nombre;
      } else if (likes.length === 2) {
        text = `${likes[0].nombre} y ${likes[1].nombre}`;
      } else {
        text = `${likes[0].nombre}, ${likes[1].nombre} y ${likes.length - 2} persona${likes.length > 3 ? 's' : ''} más`;
      }
    }
    
    return text;
  };

  // Formatear nombres de likes de comentarios
  const formatCommentLikesText = (likes, currentUser) => {
    if (!likes || likes.length === 0) return '';
    
    const currentUserLike = likes.find(like => like.email === currentUser?.email);
    const otherLikes = likes.filter(like => like.email !== currentUser?.email);
    
    let text = '';
    
    if (currentUserLike) {
      text = 'Tú';
      if (otherLikes.length > 0) {
        text += `, ${otherLikes[0]?.nombre || 'Usuario'}`;
        if (otherLikes.length > 1) {
          text += ` y ${otherLikes.length - 1} persona${otherLikes.length > 2 ? 's' : ''} más`;
        }
      }
    } else {
      if (likes.length === 1) {
        text = likes[0]?.nombre || 'Usuario';
      } else if (likes.length === 2) {
        text = `${likes[0]?.nombre || 'Usuario'} y ${likes[1]?.nombre || 'Usuario'}`;
      } else {
        text = `${likes[0]?.nombre || 'Usuario'}, ${likes[1]?.nombre || 'Usuario'} y ${likes.length - 2} persona${likes.length > 3 ? 's' : ''} más`;
      }
    }
    
    return text;
  };

  const tabs = [
    { id: 'mis-recomendaciones', label: 'Recomendaciones', icon: FaRegNewspaper },
    { id: 'eventos', label: 'Eventos', icon: FaRegCalendarAlt },
    ...(cartItems.length > 0 ? [{ id: 'carrito', label: 'Carrito', icon: FaShoppingCart }] : []),
    { id: 'membresia', label: 'Mi Membresía', icon: FaIdBadge },
    { id: 'perfil', label: 'Mi Perfil', icon: FaUser }
  ];

  const handleAddToCart = (item) => {
    setCartItems(prev => {
      const existingIndex = prev.findIndex(p => p.type === item.type && p.id === item.id);
      if (existingIndex >= 0) {
        const copy = [...prev];
        copy[existingIndex] = { ...copy[existingIndex], quantity: copy[existingIndex].quantity + (item.quantity || 1) };
        return copy;
      }
      return [...prev, item];
    });
    setActiveTab('carrito');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'mis-recomendaciones':
        return (
          <div className="dashboard-content">
            <div className="content-header">
              <h2>Recomendaciones de la Comunidad</h2>
              <div className="search-filter">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input 
                    type="text" 
                    placeholder="Buscar recomendaciones..." 
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <select 
                  className="category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <option value="">Todas las categorías</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.nombre}>
                      {cat.nombre}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Caja de Recomendación Rápida */}
            <div className="quick-recommendation-box">
              <form onSubmit={handleQuickRecommendation} className="quick-form">
                <div className="quick-form-header">
                  <div className="quick-title-wrapper">
                    <div className="quick-title-avatar"><FaUser /></div>
                    <input
                      type="text"
                      placeholder={titlePlaceholder}
                      value={quickRecommendation.titulo}
                      onFocus={() => setTitleFocused(true)}
                      onBlur={() => setTitleFocused(false)}
                      onChange={(e) => setQuickRecommendation(prev => ({ ...prev, titulo: e.target.value }))}
                      className="quick-title-input with-avatar"
                      maxLength={100}
                    />
                  </div>
                </div>
                <div className="quick-form-body">
                  <div className="textarea-container">
                    <textarea
                      placeholder="Describe tu recomendación..."
                      value={quickRecommendation.descripcion}
                      onChange={(e) => setQuickRecommendation(prev => ({ ...prev, descripcion: e.target.value }))}
                      className="quick-description-input"
                      maxLength={500}
                      rows={2}
                    />
                    <button
                      type="button"
                      className="emoji-button"
                      onClick={toggleEmojiPicker}
                      title="Agregar emoji"
                     >
                       <span className="emoji-icon">😊</span>
                     </button>
                  </div>
                  {showQuickEmojiPicker && (
                    <>
                      <div className="emoji-picker-overlay" onClick={() => setShowQuickEmojiPicker(false)}></div>
                      <div className="emoji-picker-container">
                        {renderEmojiPicker(handleEmojiClick)}
                      </div>
                    </>
                  )}
                </div>
                <div className="quick-form-footer">
                  {(() => {
                    const needCategoryWarning =
                      (quickRecommendation.titulo || '').trim() !== '' &&
                      (quickRecommendation.descripcion || '').trim() !== '' &&
                      !(quickRecommendation.categoria || '').trim();
                    return (
                      <>
                        <select
                          value={quickRecommendation.categoria}
                          onChange={(e) => setQuickRecommendation(prev => ({ ...prev, categoria: e.target.value }))}
                          className={`quick-category-select ${needCategoryWarning ? 'warning' : ''}`}
                          required
                          aria-invalid={needCategoryWarning}
                          aria-describedby={needCategoryWarning ? 'category-help' : undefined}
                        >
                          <option value="">Seleccionar categoría</option>
                          {categories.map(cat => (
                            <option key={cat.id} value={cat.nombre}>
                              {cat.nombre}
                            </option>
                          ))}
                        </select>
                        {needCategoryWarning && (
                          <small id="category-help" className="category-help-text">Selecciona una categoría para continuar</small>
                        )}
                      </>
                    );
                  })()}
                  <div className="link-input-container">
                    <input
                      type="text"
                      placeholder="Link ej. facebook.com, instagram.com/pagina o youtube.com"
                      value={quickRecommendation.enlace_url}
                      onChange={(e) => setQuickRecommendation(prev => ({ ...prev, enlace_url: e.target.value }))}
                      className="quick-link-input"
                    />
                    {quickRecommendation.enlace_url && (
                      <div className="url-preview">
                        <small>Se abrirá como: {normalizeUrl(quickRecommendation.enlace_url)}</small>
                      </div>
                    )}
                  </div>
                  <button 
                    type="submit" 
                    className="quick-submit-btn"
                    disabled={
                      quickLoading ||
                      !quickRecommendation.titulo.trim() ||
                      !quickRecommendation.descripcion.trim() ||
                      !quickRecommendation.categoria
                    }
                  >
                    {quickLoading ? (
                      <div className="quick-spinner"></div>
                    ) : (
                      <>
                        <FaShare /> Publicar Recomendación
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
            
            <div className="recommendations-grid">
              {loading ? (
                <div className="loading-state">
                  <div className="spinner"></div>
                  <p>Cargando recomendaciones...</p>
                </div>
              ) : filteredRecommendations.length === 0 ? (
                <div className="empty-state">
                  <FaRegNewspaper className="empty-icon" />
                  <h3>No hay recomendaciones</h3>
                  <p>Comienza compartiendo tus lugares favoritos con la comunidad</p>
                </div>
              ) : (
                <div className="recommendations-list">
                  {filteredRecommendations.map(rec => (
                    <div key={rec.id} className="recommendation-card">
                      {/* Header estilo Facebook */}
                      <div className="post-header">
                        <div className="post-author">
                          <div className="author-avatar">
                            <FaUser />
                          </div>
                          <div className="author-details">
                            <h4 className="author-name">{rec.autor?.nombre || 'Usuario'}</h4>
                            <span className="post-time">{formatTimeAgo(rec.fecha_creacion)}</span>
                          </div>
                        </div>
                        <div className="post-category">
                          <span className="category-badge" style={{ 
                            backgroundColor: categories.find(c => c.nombre === rec.categoria)?.color || '#d09c5a' 
                          }}>
                            {rec.categoria}
                          </span>
                        </div>
                      </div>

                      {/* Contenido del post */}
                      <div className="post-content">
                        <h3 className="post-title">{rec.titulo}</h3>
                        <p className="post-description">{rec.descripcion}</p>
                        {rec.enlace_url && (
                          <a 
                            href={normalizeUrl(rec.enlace_url)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="post-link"
                          >
                            {getLinkDisplayText(rec.enlace_url)}
                          </a>
                        )}
                      </div>

                      {/* Stats de likes y comentarios */}
                      {(rec.likes_count > 0 || rec.comments_count > 0) && (
                        <div className="post-stats">
                          {rec.likes_count > 0 && (
                            <div className="likes-info" onClick={() => openLikesModal(rec)}>
                              <FaThumbsUp className="likes-icon" />
                              <span className="likes-text">
                                {formatLikesText(likesData[rec.id] || [], user)}
                              </span>
                            </div>
                          )}
                          {rec.comments_count > 0 && (
                            <div className="comments-info" onClick={() => toggleInlineComments(rec.id)} title="Ver comentarios">
                              <span className="comments-count">
                                {rec.comments_count} comentario{rec.comments_count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Botones de acción */}
                      <div className="post-actions">
                        <button 
                          className={`action-btn like-btn ${rec.user_liked ? 'liked' : ''}`}
                          onClick={() => handleLike(rec.id)}
                          disabled={likesLoading[rec.id]}
                        >
                          <FaThumbsUp />
                          <span>Me gusta</span>
                        </button>
                        <button 
                          className="action-btn comment-btn"
                          onClick={() => toggleInlineComments(rec.id)}
                        >
                          <FaComment />
                          <span>Comentar</span>
                        </button>
                        <button 
                          className="action-btn share-btn"
                          onClick={() => openShareModal(rec)}
                        >
                          <FaShare />
                          <span>Compartir</span>
                        </button>
                      </div>

                      {/* Comentarios inline */}
                      {showInlineComments[rec.id] && (
                        <div className="inline-comments" data-recommendation-id={rec.id}>
                          {/* Lista de comentarios */}
                          {inlineComments[rec.id] && inlineComments[rec.id].length > 0 && (
                            <div className="comments-list">
                              {inlineComments[rec.id].map(comment => 
                                comment ? (
                                  <div key={comment.id} className="comment-item" data-comment-id={comment.id}>

                                  <div className="comment-avatar">

                                    <FaUser />

                                  </div>

                                  <div className="comment-content">

                                    <div className="comment-header">

                                      <span className="comment-author">{comment.autor?.nombre || 'Usuario'}</span>

                                      <span className="comment-time">{formatTimeAgo(comment.fecha_creacion)}</span>

                                    </div>

                                    <p className="comment-text">{comment.comentario}</p>

                                    

                                    {/* Acciones del comentario */}

                                    <div className="comment-actions">

                                      <button 

                                        className={`comment-action-btn like-btn ${commentLikes[comment.id] > 0 ? 'liked' : ''}`}

                                        onClick={() => handleCommentLike(comment.id)}

                                        disabled={commentLikesLoading[comment.id]}

                                      >

                                        <FaThumbsUp />

                                        <span>

                                          {commentLikes[comment.id] > 0 ? commentLikes[comment.id] : ''}

                                        </span>

                                      </button>

                                      

                                      <button 

                                        className="comment-action-btn reply-btn"

                                        onClick={() => toggleReplies(comment.id)}

                                      >

                                        <FaReply />

                                        <span>Responder</span>

                                      </button>

                                      

                                      {/* Mostrar likes del comentario */}

                                      {commentLikes[comment.id] > 0 && (

                                        <div className="comment-likes-info">

                                          <FaThumbsUp className="comment-likes-icon" />

                                          <span className="comment-likes-text">

                                            {formatCommentLikesText(commentLikesData[comment.id] || [], user)}

                                          </span>

                                        </div>

                                      )}

                                    </div>

                                    

                                    {/* Respuestas del comentario */}

                                    {showReplies[comment.id] && (

                                      <div className="comment-replies">

                                        {repliesLoading[comment.id] ? (

                                          <div className="loading-replies">

                                            <div className="spinner"></div>

                                            <span>Cargando respuestas...</span>

                                          </div>

                                        ) : (

                                          <>

                                            {commentReplies[rec.id] && commentReplies[rec.id][comment.id] && commentReplies[rec.id][comment.id].length > 0 && (

                                              <div className="replies-list">

                                                {commentReplies[rec.id][comment.id].map(reply => 
                                                  reply ? (
                                                    <div key={reply.id} className="reply-item">
                                                      <div className="reply-avatar">
                                                        <FaUser />
                                                      </div>
                                                      <div className="reply-content">
                                                        <div className="reply-header">
                                                          <span className="reply-author">{reply.autor?.nombre || 'Usuario'}</span>
                                                          <span className="reply-time">{formatTimeAgo(reply.fecha_creacion)}</span>
                                                        </div>
                                                        <p className="reply-text">{reply.comentario || ''}</p>
                                                      </div>
                                                    </div>
                                                  ) : null
                                                )}

                                              </div>

                                            )}

                                            

                                            {/* Formulario de nueva respuesta */}

                                            <div className="new-reply-form">

                                              <div className="reply-input-container">

                                                <div className="reply-avatar">

                                                  <FaUser />

                                                </div>

                                                <input

                                                  type="text"

                                                  placeholder="Escribe una respuesta..."

                                                  value={newReply[comment.id] || ''}

                                                  onChange={(e) => handleReplyChange(comment.id, e.target.value)}

                                                  className="reply-input"

                                                  onKeyPress={(e) => {

                                                    if (e.key === 'Enter') {

                                                      handleReplySubmit(comment.id);

                                                    }

                                                  }}

                                                />

                                                <button

                                                  className="reply-submit-btn"

                                                  onClick={() => handleReplySubmit(comment.id)}

                                                  disabled={!newReply[comment.id]?.trim() || replySending[comment.id]}

                                                >

                                                  {replySending[comment.id] ? (

                                                    <div className="spinner-small"></div>

                                                  ) : (

                                                    <FaReply />

                                                  )}

                                                </button>

                                              </div>

                                            </div>

                                          </>

                                        )}

                                      </div>

                                    )}

                                  </div>

                                </div>
                                ) : null
                              )}
                            </div>
                          )}

                          {/* Formulario de nuevo comentario */}
                          <div className="new-comment-form">
                            <div className="comment-input-container modern">
                              <div className="comment-avatar">
                                <FaUser />
                              </div>
                              <div className="comment-input-wrapper">
                                <input
                                  type="text"
                                  placeholder="Comentar"
                                  value={newInlineComment[rec.id] || ''}
                                  onChange={(e) => handleInlineCommentChange(rec.id, e.target.value)}
                                  className="comment-input modern"
                                  onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                      handleInlineCommentSubmit(rec.id);
                                    }
                                  }}
                                />
                                <button
                                  type="button"
                                  className="emoji-button"
                                  aria-label="Agregar emoji"
                                  title="Agregar emoji"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    setCommentEmoji(prev => ({
                                      visible: !prev.visible || prev.recId !== rec.id,
                                      top: rect.bottom + window.scrollY + 8,
                                      left: rect.left + window.scrollX,
                                      recId: rec.id
                                    }));
                                  }}
                                >
                                  <span className="emoji-icon">😊</span>
                                </button>
                              </div>
                              {commentEmoji.visible && commentEmoji.recId === rec.id && (
                                <div
                                  className="emoji-picker-container"
                                  style={{ position: 'absolute', top: commentEmoji.top, left: commentEmoji.left, zIndex: 2000 }}
                                >
                                  {renderEmojiPicker((emoji) => {
                                    const toAdd = emoji.emoji || '';
                                    setNewInlineComment(prev => ({
                                      ...prev,
                                      [rec.id]: (prev[rec.id] || '') + toAdd
                                    }));
                                  })}
                                </div>
                              )}
                                <button
                                  className="comment-submit-btn"
                                  onClick={() => handleInlineCommentSubmit(rec.id)}
                                  disabled={!newInlineComment[rec.id]?.trim() || commentSending[rec.id]}
                                >
                                  {commentSending[rec.id] ? (
                                    <>
                                      <div className="spinner-small"></div>
                                      <span>Enviando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FaComment />
                                      <span>Comentar</span>
                                    </>
                                  )}
                                </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        );

      case 'eventos':
        return (
          <div className="dashboard-content">
            <div className="content-header">
              <h2>Próximos Eventos:</h2>
            </div>
            <EventsSection onAddToCart={handleAddToCart} />
          </div>
        );

      case 'carrito':
        return (
          <div className="dashboard-content">
            <div className="content-header">
              <h2>Carrito</h2>
            </div>
            {cartItems.length === 0 ? (
              <div className="empty-state">
                <FaShoppingCart className="empty-icon" />
                <h3>Tu carrito está vacío</h3>
                <p>Agrega boletos desde la sección de Eventos.</p>
              </div>
            ) : (
              <div className="cart-list">
                {cartItems.map((item, idx) => (
                  <div key={idx} className="cart-item">
                    <div className="cart-item-info">
                      <strong>{item.title}</strong>
                      <span>Cantidad: {item.quantity}</span>
                    </div>
                    <div className="cart-item-price">
                      ${Number(item.price * item.quantity).toFixed(2)} MXN
                    </div>
                  </div>
                ))}
                <div className="cart-total">
                  Total: ${cartItems.reduce((sum, it) => sum + (it.price * it.quantity), 0).toFixed(2)} MXN
                </div>
              </div>
            )}
          </div>
        );

      case 'perfil':
        return (
          <div className="dashboard-content">
            <div className="content-header">
              <h2>Mi Perfil</h2>
            </div>
            {/* Subpestañas: Perfil | Configuración */}
            <div className="subtabs">
              <button className={`subtab ${profileTab === 'perfil' ? 'active' : ''}`} onClick={() => setProfileTab('perfil')}>Perfil</button>
              <button className={`subtab ${profileTab === 'config' ? 'active' : ''}`} onClick={() => setProfileTab('config')}>Configuración</button>
            </div>
            {profileTab === 'perfil' ? (
              <div className="profile-form">
                <div className="profile-avatar">
                  <div className="avatar-placeholder">
                    <FaUser />
                  </div>
                  <button className="change-avatar-btn">Cambiar Foto</button>
                </div>
                <form>
                  <div className="form-group">
                    <label>Nombre completo</label>
                    <input type="text" value={profileForm.nombre} onChange={(e)=>{ setProfileForm(p=>({ ...p, nombre: e.target.value })); setProfileDirty(true); }} placeholder="Tu nombre completo" />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={profileForm.email} placeholder="tu@email.com" disabled className="input-disabled" />
                  </div>
                  <div className="form-group">
                    <label>Teléfono Móvil</label>
                    <input 
                      type="tel" 
                      value={profileForm.telefono}
                      onChange={(e)=>{ 
                        const value = e.target.value;
                        setProfileForm(p=>({ ...p, telefono: value })); 
                        setProfileDirty(true);
                        const digits = value.replace(/\D/g,'');
                        if(digits.length === 0){ setPhoneError(''); }
                        else if(!/^\d{10}$/.test(digits)) { setPhoneError('Ingresa 10 dígitos (solo números)'); }
                        else { setPhoneError(''); }
                      }} 
                      placeholder="Tu número de teléfono móvil"
                      className={phoneError ? 'input-error' : ''}
                    />
                    {phoneError && <small className="field-error-text">{phoneError}</small>}
                  </div>
                  <div className="form-actions">
                    <button type="button" className="primary-btn" disabled={!profileDirty || profileSaving || !!phoneError} onClick={async ()=>{
                      if(!profileDirty) return;
                      try{
                        setProfileSaving(true);
                        const resp = await updateProfile({ nombre: profileForm.nombre, telefono: profileForm.telefono });
                        if(resp?.success){
                          setProfileDirty(false);
                        } else {
                          alert(resp?.message || 'No se pudo guardar');
                        }
                      }catch(err){
                        alert('Error guardando perfil');
                      }finally{
                        setProfileSaving(false);
                      }
                    }}>{profileSaving ? 'Guardando...' : 'Guardar Cambios'}</button>
                  </div>
                </form>
              </div>
            ) : (
              <div className="settings-form">
                <div className="setting-group">
                  <h3>Notificaciones</h3>
                  <div className="setting-item">
                    <label>
                      <input type="checkbox" defaultChecked />
                      Recibir notificaciones por email
                    </label>
                  </div>
                  <div className="setting-item">
                    <label>
                      <input type="checkbox" defaultChecked />
                      Notificaciones de nuevas recomendaciones
                    </label>
                  </div>
                </div>
                <div className="setting-group">
                  <h3>Privacidad</h3>
                  <div className="setting-item">
                    <label>
                      <input type="checkbox" defaultChecked />
                      Perfil público
                    </label>
                  </div>
                </div>
                <div className="setting-group">
                  <h3>Cuenta</h3>
                  <button className="danger-btn">Eliminar Cuenta</button>
                </div>
              </div>
            )}
          </div>
        );

      case 'membresia':
        return (
          <div className="dashboard-content">
            <div className="empty-state">
              <h3>Próximamente</h3>
            </div>
          </div>
        );

      // configuración fusionada dentro de 'perfil'

      default:
        return null;
    }
  };

  return (
    <div className="dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
            <div className="header-left">
              <div className="logo" onClick={handleLogoClick} style={{ cursor: 'pointer' }} title="Ir a página principal">
                <img src="/images/RecomiendaT-logo.png" alt="RecomiendaT" />
              </div>
            </div>
          <div className="header-right">
            <div className="user-info-link" style={{ cursor: 'default' }}>
              <div className="user-avatar">
                <FaUser />
              </div>
              <div className="user-details">
                <span className="user-name">{user?.nombre || 'Usuario'}</span>
                <span className="user-email">{user?.email || ''}</span>
              </div>
            </div>
            <button 
              className="logout-btn"
              onClick={handleLogout}
              disabled={loading}
              title="Cerrar sesión"
            >
              <FaSignOutAlt />
            </button>
          </div>
        </div>
      </header>

      {/* Sidebar Desktop */}
      <aside className="dashboard-sidebar">
        <nav className="sidebar-nav">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="nav-icon-wrapper">
                  <Icon className="nav-icon" />
                  {tab.id === 'carrito' && cartCount > 0 && (
                    <span className="cart-badge">{cartCount}</span>
                  )}
                </span>
                <span className="nav-label">{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      {/* Main Content */}
      <main className="dashboard-main">
        <div className="dashboard-content">
          {renderTabContent()}
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <div className="mobile-nav-items">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                className={`mobile-nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="mobile-nav-icon-wrapper">
                  <Icon className="mobile-nav-icon" />
                  {tab.id === 'carrito' && cartCount > 0 && (
                    <span className="cart-badge">{cartCount}</span>
                  )}
                </span>
                <span className="mobile-nav-label">{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Modal de Comentarios */}
      {selectedRecommendation && (
        <div className="modal-overlay" onClick={() => setSelectedRecommendation(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Comentarios - {selectedRecommendation.titulo}</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setSelectedRecommendation(null)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="comments-list">
                {commentsLoading ? (
                  <div className="loading-state">
                    <div className="spinner"></div>
                    <p>Cargando comentarios...</p>
                  </div>
                ) : comments.length === 0 ? (
                  <div className="empty-state">
                    <FaComment className="empty-icon" />
                    <h3>No hay comentarios aún</h3>
                    <p>¡Sé el primero en comentar!</p>
                  </div>
                ) : (
                  comments.map(comment => (
                    <div key={comment.id} className="comment-item">
                      <div className="comment-author">
                        <FaUser className="comment-author-icon" />
                        <span>{comment.autor?.nombre || 'Usuario'}</span>
                      </div>
                      <div className="comment-content">
                        <p>{comment.comentario}</p>
                        <div className="comment-meta">
                          <span className="comment-date">
                            {new Date(comment.fecha_creacion).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <form className="comment-form" onSubmit={handleCreateComment}>
              <div className="comment-input-group">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Escribe tu comentario..."
                  rows="3"
                  required
                />
                <button 
                  type="submit" 
                  className="btn btn-primary comment-submit-btn"
                  disabled={commentLoading || !newComment.trim()}
                >
                  {commentLoading ? 'Enviando...' : 'Comentar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Likes */}
      {showLikesModal && selectedRecommendation && (
        <div className="modal-overlay" onClick={() => setShowLikesModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Me gusta - {selectedRecommendation.titulo}</h3>
              <button 
                className="close-modal-btn"
                onClick={() => setShowLikesModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="likes-list">
                {likesData[selectedRecommendation.id]?.length === 0 ? (
                  <div className="empty-state">
                    <FaThumbsUp className="empty-icon" />
                    <h3>No hay likes aún</h3>
                    <p>¡Sé el primero en dar like!</p>
                  </div>
                ) : (
                  likesData[selectedRecommendation.id]?.map((like, index) => (
                    <div key={index} className="like-item">
                      <div className="like-user">
                        <FaUser className="like-user-icon" />
                        <span>{like.nombre}</span>
                      </div>
                      <div className="like-date">
                        {new Date(like.fecha_creacion).toLocaleDateString('es-ES')}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Compartir */}
      {showShareModal && (
        <div className="modal-overlay" onClick={closeShareModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Compartir Recomendación</h3>
              <button className="close-modal-btn" onClick={closeShareModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="share-content">
                <div className="share-preview">
                  <h4>{shareRecommendation?.titulo}</h4>
                  <p>{shareRecommendation?.descripcion}</p>
                  <span className="share-author">Por {shareRecommendation?.autor?.nombre || 'Usuario'}</span>
                </div>
                
                <div className="share-url-container">
                  <label>Enlace para compartir:</label>
                  <div className="url-input-group">
                    <input 
                      type="text" 
                      value={shareUrl} 
                      readOnly 
                      className="share-url-input"
                    />
                    <button 
                      className="copy-btn"
                      onClick={copyToClipboard}
                      title="Copiar enlace"
                    >
                      📋 Copiar
                    </button>
                  </div>
                </div>

                <div className="social-share-buttons">
                  <h4>Compartir en redes sociales:</h4>
                  <div className="social-buttons-grid">
                    <button 
                      className="social-btn facebook-btn"
                      onClick={() => shareToSocial('facebook')}
                    >
                      📘 Facebook
                    </button>
                    <button 
                      className="social-btn twitter-btn"
                      onClick={() => shareToSocial('twitter')}
                    >
                      🐦 Twitter
                    </button>
                    <button 
                      className="social-btn whatsapp-btn"
                      onClick={() => shareToSocial('whatsapp')}
                    >
                      💬 WhatsApp
                    </button>
                    <button 
                      className="social-btn telegram-btn"
                      onClick={() => shareToSocial('telegram')}
                    >
                      ✈️ Telegram
                    </button>
                    <button 
                      className="social-btn linkedin-btn"
                      onClick={() => shareToSocial('linkedin')}
                    >
                      💼 LinkedIn
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

